import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../utils/api';
import { useLang } from '../context/LangContext';
import './Appointments.css';

const SLOT_DURATIONS = [15, 30, 45, 60, 90, 120];
const GRID_DURATIONS = [15, 30, 45, 60];

const TIMEZONES = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'America/Honolulu',
  'America/Bogota',
  'America/Mexico_City',
  'America/Buenos_Aires',
  'America/Sao_Paulo',
  'America/Santiago',
  'Europe/London',
  'Europe/Madrid',
  'UTC',
];

/** DB day_of_week: 0 Sun .. 6 Sat — display Sun→Sat */
const DAYS_SUN_FIRST = [0, 1, 2, 3, 4, 5, 6];

function dayLabels(lang) {
  const en = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const es = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
  return lang === 'en' ? en : es;
}

function defaultWeekHours() {
  return DAYS_SUN_FIRST.map((d) => ({
    day_of_week: d,
    start_time: d >= 1 && d <= 5 ? '09:00' : '09:00',
    end_time: d >= 1 && d <= 5 ? '17:00' : '17:00',
    is_active: d >= 1 && d <= 5 ? 1 : 0,
  }));
}

function mergeHoursFromApi(rows) {
  const by = {};
  (rows || []).forEach((r) => {
    by[r.day_of_week] = r;
  });
  return DAYS_SUN_FIRST.map((d) => {
    if (by[d]) {
      return {
        day_of_week: d,
        start_time: String(by[d].start_time || '09:00:00').slice(0, 5),
        end_time: String(by[d].end_time || '17:00:00').slice(0, 5),
        is_active: Number(by[d].is_active) ? 1 : 0,
      };
    }
    return defaultWeekHours().find((x) => x.day_of_week === d);
  });
}

function toSqlTime(t) {
  if (!t) return '09:00:00';
  const s = String(t).trim();
  return s.length === 5 ? `${s}:00` : s;
}

/** 12-hour display for dashboard slots */
function formatApptDateIso(appt) {
  if (!appt?.appointment_date) return '';
  const d = appt.appointment_date;
  if (typeof d === 'string') return d.slice(0, 10);
  try {
    return new Date(d).toISOString().slice(0, 10);
  } catch {
    return '';
  }
}

function fmtTime12(t) {
  if (t == null || t === '') return '';
  const s = String(t).slice(0, 5);
  const [hh, mm] = s.split(':').map((x) => parseInt(x, 10));
  if (Number.isNaN(hh)) return s;
  const h12 = hh % 12 || 12;
  const ampm = hh >= 12 ? 'PM' : 'AM';
  return `${h12}:${String(mm).padStart(2, '0')} ${ampm}`;
}

/** Backend day-view timeline items (kind) or legacy slot rows */
function normalizeDayItem(item) {
  if (item && item.kind) return item;
  if (!item) return null;
  if (item.state === 'closed' || item.blocked_slot_id) {
    return {
      kind: 'closed',
      start_time: item.start_time,
      end_time: item.end_time,
      state: 'closed',
      appointment: item.appointment || null,
      blocked_slot_id: item.blocked_slot_id,
    };
  }
  if (item.appointment) {
    return {
      kind: 'appointment',
      start_time: item.start_time,
      end_time: item.end_time,
      state: item.state,
      appointment: item.appointment,
      blocked_slot_id: null,
    };
  }
  return {
    kind: 'free',
    start_time: item.start_time,
    end_time: item.end_time,
    state: item.state,
    appointment: null,
    blocked_slot_id: null,
  };
}

export default function Appointments() {
  const { lang } = useLang();
  const labels = dayLabels(lang);

  const [tab, setTab] = useState('today');

  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [dayView, setDayView] = useState(null);
  /** Set when GET day-view fails — avoids showing “no hours” while dayView is null */
  const [dayViewError, setDayViewError] = useState(null);
  const [loadingDay, setLoadingDay] = useState(false);

  const [settings, setSettings] = useState({
    enabled: false,
    services_enabled: false,
    grid_duration: 30,
    slot_duration: 30,
    advance_days: 7,
    timezone: 'America/New_York',
  });
  const [servicesList, setServicesList] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [serviceForm, setServiceForm] = useState({ name: '', duration: 30, price: '' });
  const [savingService, setSavingService] = useState(false);
  const [editingServiceId, setEditingServiceId] = useState(null);
  const serviceFormRef = useRef(null);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  const [hours, setHours] = useState(() => defaultWeekHours());
  const [loadingHours, setLoadingHours] = useState(false);
  const [savingHours, setSavingHours] = useState(false);

  const [blocked, setBlocked] = useState([]);
  const [loadingBlocked, setLoadingBlocked] = useState(false);
  const [blockedDate, setBlockedDate] = useState('');
  const [blockedReason, setBlockedReason] = useState('');
  const [addingBlocked, setAddingBlocked] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalForm, setModalForm] = useState({
    client_name: '',
    client_phone: '',
    appointment_date: '',
    appointment_time: '',
    notes: '',
    service_id: '',
  });
  const [slotOptions, setSlotOptions] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [savingAppt, setSavingAppt] = useState(false);

  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleAppt, setRescheduleAppt] = useState(null);
  const [rescheduleForm, setRescheduleForm] = useState({
    new_date: '',
    new_time: '',
    new_service_id: '',
  });
  const [rescheduleSlots, setRescheduleSlots] = useState([]);
  const [rescheduleSlotsLoading, setRescheduleSlotsLoading] = useState(false);
  const [savingReschedule, setSavingReschedule] = useState(false);

  const [banner, setBanner] = useState(null);

  const showError = (msg) => {
    setBanner({ type: 'error', msg });
    setTimeout(() => setBanner(null), 6000);
  };
  const showOk = (msg) => {
    setBanner({ type: 'ok', msg });
    setTimeout(() => setBanner(null), 4000);
  };

  const fetchDayView = useCallback(async () => {
    setLoadingDay(true);
    setDayViewError(null);
    try {
      const res = await api.get(`/appointments/day-view?date=${selectedDate}`);
      if (res.data?.success) {
        setDayView(res.data.data || null);
        setDayViewError(null);
      } else {
        setDayView(null);
        const msg = res.data?.message || (lang === 'en' ? 'Failed to load day' : 'Error al cargar el día');
        setDayViewError(msg);
      }
    } catch (e) {
      setDayView(null);
      const msg =
        e.response?.data?.message ||
        (e.response?.status === 404
          ? lang === 'en'
            ? 'Calendar view not found — redeploy the latest API or contact support.'
            : 'Vista de agenda no disponible — despliega la última API o contacta soporte.'
          : lang === 'en'
            ? 'Failed to load day'
            : 'Error al cargar el día');
      setDayViewError(msg);
    }
    setLoadingDay(false);
  }, [selectedDate, lang]);

  const loadSettings = useCallback(async () => {
    setLoadingSettings(true);
    try {
      const res = await api.get('/appointments/settings');
      if (res.data?.success && res.data.data) {
        const d = res.data.data;
        setSettings({
          enabled: !!Number(d.enabled),
          services_enabled: !!Number(d.services_enabled),
          grid_duration: GRID_DURATIONS.includes(Number(d.grid_duration))
            ? Number(d.grid_duration)
            : 30,
          slot_duration: Number(d.slot_duration) || 30,
          advance_days: Number(d.advance_days) || 7,
          timezone: d.timezone || 'America/New_York',
        });
      }
    } catch {
      showError(lang === 'en' ? 'Failed to load settings' : 'Error al cargar configuración');
    }
    setLoadingSettings(false);
  }, [lang]);

  const loadServices = useCallback(async () => {
    setLoadingServices(true);
    try {
      const res = await api.get('/appointments/services');
      if (res.data?.success) setServicesList(res.data.data || []);
    } catch {
      setServicesList([]);
    }
    setLoadingServices(false);
  }, []);

  const loadHours = useCallback(async () => {
    setLoadingHours(true);
    try {
      const res = await api.get('/appointments/hours');
      if (res.data?.success && res.data.data?.hours?.length) {
        setHours(mergeHoursFromApi(res.data.data.hours));
      } else {
        setHours(defaultWeekHours());
      }
    } catch {
      setHours(defaultWeekHours());
      showError(lang === 'en' ? 'Failed to load hours' : 'Error al cargar horarios');
    }
    setLoadingHours(false);
  }, [lang]);

  const loadBlocked = useCallback(async () => {
    setLoadingBlocked(true);
    try {
      const res = await api.get('/appointments/blocked-dates');
      if (res.data?.success) setBlocked(res.data.data || []);
    } catch {
      showError(lang === 'en' ? 'Failed to load blocked dates' : 'Error al cargar fechas bloqueadas');
    }
    setLoadingBlocked(false);
  }, [lang]);

  useEffect(() => {
    if (tab === 'today') {
      fetchDayView();
      loadSettings();
    }
  }, [tab, fetchDayView, loadSettings]);

  useEffect(() => {
    if (tab === 'settings') loadSettings();
  }, [tab, loadSettings]);

  useEffect(() => {
    if (tab === 'settings' || tab === 'today') loadServices();
  }, [tab, loadServices]);

  useEffect(() => {
    if (tab === 'schedule') loadHours();
  }, [tab, loadHours]);

  useEffect(() => {
    if (tab === 'blocked') loadBlocked();
  }, [tab, loadBlocked]);

  const fetchRescheduleSlots = useCallback(async (dateStr, serviceId) => {
    if (!dateStr) return;
    setRescheduleSlotsLoading(true);
    try {
      let url = `/appointments/available-slots?date=${dateStr}`;
      if (rescheduleAppt?.id) {
        url += `&exclude_appointment_id=${encodeURIComponent(rescheduleAppt.id)}`;
      }
      if (serviceId != null && serviceId !== '') {
        url += `&service_id=${encodeURIComponent(serviceId)}`;
      }
      const res = await api.get(url);
      if (res.data?.success && res.data.data) {
        const slots = res.data.data.slots || [];
        setRescheduleSlots(slots);
        setRescheduleForm((prev) => {
          const t = String(prev.new_time || '').slice(0, 5);
          const norm = (x) => String(x || '').slice(0, 5);
          const hit = slots.find((s) => norm(s) === t);
          if (hit) return { ...prev, new_time: norm(hit) };
          if (slots.length) return { ...prev, new_time: norm(slots[0]) };
          return { ...prev, new_time: '' };
        });
      } else {
        setRescheduleSlots([]);
        setRescheduleForm((prev) => ({ ...prev, new_time: '' }));
      }
    } catch {
      setRescheduleSlots([]);
      setRescheduleForm((prev) => ({ ...prev, new_time: '' }));
    }
    setRescheduleSlotsLoading(false);
  }, [rescheduleAppt?.id]);

  const fetchSlotsForModal = useCallback(async (dateStr, serviceId) => {
    if (!dateStr) return;
    setSlotsLoading(true);
    setSlotOptions([]);
    try {
      let url = `/appointments/available-slots?date=${dateStr}`;
      if (serviceId != null && serviceId !== '') {
        url += `&service_id=${encodeURIComponent(serviceId)}`;
      }
      const res = await api.get(url);
      if (res.data?.success && res.data.data) {
        const slots = res.data.data.slots || [];
        setSlotOptions(slots);
        setModalForm((prev) => {
          const next =
            slots.includes(prev.appointment_time) ? prev.appointment_time : slots[0] || '';
          return { ...prev, appointment_time: next };
        });
      } else {
        setSlotOptions([]);
        setModalForm((prev) => ({ ...prev, appointment_time: '' }));
      }
    } catch {
      setModalForm((prev) => ({ ...prev, appointment_time: '' }));
    }
    setSlotsLoading(false);
  }, []);

  useEffect(() => {
    if (modalOpen && modalForm.appointment_date) {
      const sid =
        settings.services_enabled && modalForm.service_id ? modalForm.service_id : '';
      fetchSlotsForModal(modalForm.appointment_date, sid || null);
    }
  }, [
    modalOpen,
    modalForm.appointment_date,
    modalForm.service_id,
    settings.services_enabled,
    fetchSlotsForModal,
  ]);

  useEffect(() => {
    if (!rescheduleOpen || !rescheduleForm.new_date || !rescheduleAppt?.id) return;
    const sid =
      settings.services_enabled && rescheduleForm.new_service_id ? rescheduleForm.new_service_id : '';
    fetchRescheduleSlots(rescheduleForm.new_date, sid || null);
  }, [
    rescheduleOpen,
    rescheduleForm.new_date,
    rescheduleForm.new_service_id,
    settings.services_enabled,
    rescheduleAppt?.id,
    fetchRescheduleSlots,
  ]);

  const openModal = () => {
    const today = new Date().toISOString().slice(0, 10);
    setModalForm({
      client_name: '',
      client_phone: '',
      appointment_date: today,
      appointment_time: '',
      notes: '',
      service_id: '',
    });
    setModalOpen(true);
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.post('/appointments/settings', {
        enabled: settings.enabled ? 1 : 0,
        services_enabled: settings.services_enabled ? 1 : 0,
        grid_duration: settings.grid_duration,
        slot_duration: settings.slot_duration,
        advance_days: settings.advance_days,
        timezone: settings.timezone,
      });
      showOk(lang === 'en' ? 'Settings saved' : 'Configuración guardada');
    } catch (e) {
      showError(e.response?.data?.message || (lang === 'en' ? 'Save failed' : 'Error al guardar'));
    }
    setSavingSettings(false);
  };

  const saveService = async () => {
    if (!serviceForm.name.trim()) {
      showError(lang === 'en' ? 'Service name is required' : 'El nombre del servicio es obligatorio');
      return;
    }
    setSavingService(true);
    try {
      const dur = parseInt(String(serviceForm.duration), 10);
      if (Number.isNaN(dur) || dur < 1) {
        showError(lang === 'en' ? 'Invalid duration' : 'Duración no válida');
        setSavingService(false);
        return;
      }
      const priceRaw = String(serviceForm.price || '').trim();
      const body = {
        name: serviceForm.name.trim(),
        duration: dur,
        price: priceRaw === '' ? null : priceRaw,
      };
      if (editingServiceId) {
        await api.put(`/appointments/services/${editingServiceId}`, body);
        showOk(lang === 'en' ? 'Service updated' : 'Servicio actualizado');
        setEditingServiceId(null);
      } else {
        await api.post('/appointments/services', body);
        showOk(lang === 'en' ? 'Service added' : 'Servicio agregado');
      }
      setServiceForm({ name: '', duration: 30, price: '' });
      await loadServices();
    } catch (e) {
      showError(e.response?.data?.message || (lang === 'en' ? 'Save failed' : 'Error al guardar'));
    }
    setSavingService(false);
  };

  const deleteService = async (id) => {
    const msg =
      lang === 'en' ? 'Remove this service? Past appointments keep the saved name.' : '¿Eliminar este servicio? Las citas pasadas conservan el nombre.';
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/appointments/services/${id}`);
      showOk(lang === 'en' ? 'Removed' : 'Eliminado');
      if (editingServiceId === id) {
        setEditingServiceId(null);
        setServiceForm({ name: '', duration: 30, price: '' });
      }
      await loadServices();
    } catch {
      showError(lang === 'en' ? 'Delete failed' : 'Error al eliminar');
    }
  };

  const startEditService = (s) => {
    const id = s.id != null ? Number(s.id) : null;
    if (id == null || Number.isNaN(id)) return;
    setEditingServiceId(id);
    setServiceForm({
      name: s.name || '',
      duration: Number(s.duration) || 30,
      price: s.price != null && s.price !== '' ? String(s.price) : '',
    });
    requestAnimationFrame(() => {
      serviceFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  };

  const saveHours = async () => {
    setSavingHours(true);
    try {
      const payload = {
        hours: hours.map((h) => ({
          day_of_week: h.day_of_week,
          start_time: toSqlTime(h.start_time),
          end_time: toSqlTime(h.end_time),
          is_active: h.is_active ? 1 : 0,
        })),
      };
      await api.post('/appointments/hours', payload);
      showOk(lang === 'en' ? 'Schedule saved' : 'Horarios guardados');
    } catch (e) {
      showError(e.response?.data?.message || (lang === 'en' ? 'Save failed' : 'Error al guardar'));
    }
    setSavingHours(false);
  };

  const updateHour = (day, field, val) => {
    setHours((prev) =>
      prev.map((row) => (row.day_of_week === day ? { ...row, [field]: val } : row)),
    );
  };

  const addBlocked = async () => {
    if (!blockedDate) return;
    setAddingBlocked(true);
    try {
      await api.post('/appointments/blocked-dates', {
        blocked_date: blockedDate,
        reason: blockedReason || null,
      });
      setBlockedDate('');
      setBlockedReason('');
      await loadBlocked();
      showOk(lang === 'en' ? 'Date blocked' : 'Fecha bloqueada');
    } catch (e) {
      showError(e.response?.data?.message || (lang === 'en' ? 'Could not block date' : 'No se pudo bloquear'));
    }
    setAddingBlocked(false);
  };

  const removeBlocked = async (id) => {
    const msg =
      lang === 'en' ? 'Remove this blocked date?' : '¿Quitar esta fecha bloqueada?';
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/appointments/blocked-dates/${id}`);
      await loadBlocked();
      showOk(lang === 'en' ? 'Removed' : 'Eliminado');
    } catch {
      showError(lang === 'en' ? 'Delete failed' : 'Error al eliminar');
    }
  };

  const saveNewAppointment = async () => {
    if (!modalForm.client_name.trim()) {
      showError(lang === 'en' ? 'Name is required' : 'El nombre es obligatorio');
      return;
    }
    if (
      settings.services_enabled &&
      servicesList.filter((s) => Number(s.is_active) !== 0).length > 0 &&
      !modalForm.service_id
    ) {
      showError(lang === 'en' ? 'Select a service' : 'Selecciona un servicio');
      return;
    }
    if (!modalForm.appointment_time) {
      showError(lang === 'en' ? 'Pick a time slot' : 'Elige un horario');
      return;
    }
    setSavingAppt(true);
    try {
      const payload = {
        client_name: modalForm.client_name.trim(),
        client_phone: modalForm.client_phone.trim() || null,
        appointment_date: modalForm.appointment_date,
        appointment_time: modalForm.appointment_time,
        notes: modalForm.notes.trim() || null,
      };
      if (settings.services_enabled && modalForm.service_id) {
        const svc = servicesList.find((s) => String(s.id) === String(modalForm.service_id));
        payload.service_id = Number(modalForm.service_id);
        if (svc?.name) payload.service_name = svc.name;
      }
      await api.post('/appointments', payload);
      setModalOpen(false);
      showOk(lang === 'en' ? 'Appointment created' : 'Cita creada');
      fetchDayView();
    } catch (e) {
      showError(e.response?.data?.message || (lang === 'en' ? 'Could not create' : 'No se pudo crear'));
    }
    setSavingAppt(false);
  };

  const doComplete = async (id) => {
    try {
      await api.put(`/appointments/${id}/complete`);
      fetchDayView();
      showOk(lang === 'en' ? 'Marked complete' : 'Marcada completada');
    } catch (e) {
      showError(e.response?.data?.message || (lang === 'en' ? 'Update failed' : 'Error al actualizar'));
    }
  };

  const doCancel = async (id) => {
    const msg = lang === 'en' ? 'Cancel this appointment?' : '¿Cancelar esta cita?';
    if (!window.confirm(msg)) return;
    try {
      await api.put(`/appointments/${id}/cancel`);
      fetchDayView();
      showOk(lang === 'en' ? 'Cancelled' : 'Cancelada');
    } catch (e) {
      showError(e.response?.data?.message || (lang === 'en' ? 'Update failed' : 'Error al actualizar'));
    }
  };

  const openReschedule = (appt) => {
    setRescheduleAppt(appt);
    const d = formatApptDateIso(appt) || selectedDate;
    setRescheduleForm({
      new_date: d,
      new_time: String(appt.appointment_time || '').slice(0, 5),
      new_service_id: appt.service_id != null ? String(appt.service_id) : '',
    });
    setRescheduleOpen(true);
  };

  const closeReschedule = () => {
    setRescheduleOpen(false);
    setRescheduleAppt(null);
    setRescheduleSlots([]);
  };

  const submitReschedule = async () => {
    if (!rescheduleAppt?.id) return;
    if (!rescheduleForm.new_time) {
      showError(lang === 'en' ? 'Select a time slot' : 'Selecciona un horario');
      return;
    }
    setSavingReschedule(true);
    try {
      const payload = {
        new_date: rescheduleForm.new_date,
        new_time: rescheduleForm.new_time,
      };
      if (settings.services_enabled && rescheduleForm.new_service_id) {
        payload.new_service_id = Number(rescheduleForm.new_service_id);
      }
      await api.put(`/appointments/${rescheduleAppt.id}/reschedule`, payload);
      closeReschedule();
      showOk(lang === 'en' ? 'Appointment rescheduled' : 'Cita reprogramada');
      fetchDayView();
    } catch (e) {
      showError(
        e.response?.data?.message || (lang === 'en' ? 'Could not reschedule' : 'No se pudo reprogramar'),
      );
    }
    setSavingReschedule(false);
  };

  const openModalForSlot = (slot) => {
    setModalForm({
      client_name: '',
      client_phone: '',
      appointment_date: selectedDate,
      appointment_time: String(slot.start_time).slice(0, 5),
      notes: '',
      service_id: '',
    });
    setModalOpen(true);
  };

  const closeSlotAction = async (slot) => {
    const msg =
      lang === 'en'
        ? 'Close this time slot for new bookings?'
        : '¿Cerrar este horario para nuevas citas?';
    if (!window.confirm(msg)) return;
    try {
      await api.post('/appointments/blocked-slots', {
        blocked_date: selectedDate,
        start_time: slot.start_time,
      });
      showOk(lang === 'en' ? 'Slot closed' : 'Horario cerrado');
      fetchDayView();
    } catch (e) {
      showError(e.response?.data?.message || (lang === 'en' ? 'Could not close slot' : 'No se pudo cerrar'));
    }
  };

  const reopenSlotAction = async (slot) => {
    if (!slot.blocked_slot_id) return;
    try {
      await api.delete(`/appointments/blocked-slots/${slot.blocked_slot_id}`);
      showOk(lang === 'en' ? 'Slot reopened' : 'Horario reabierto');
      fetchDayView();
    } catch (e) {
      showError(e.response?.data?.message || (lang === 'en' ? 'Could not reopen' : 'No se pudo reabrir'));
    }
  };

  const releaseCancelledSlot = async (appointmentId) => {
    const msg =
      lang === 'en'
        ? 'Remove this cancelled appointment from the list? The time slot will be fully available for new bookings.'
        : '¿Eliminar esta cita cancelada de la lista? El horario quedará libre para nuevas reservas.';
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/appointments/${appointmentId}`);
      showOk(lang === 'en' ? 'Appointment removed' : 'Cita eliminada');
      fetchDayView();
    } catch (e) {
      showError(
        e.response?.data?.message ||
          (lang === 'en' ? 'Could not remove appointment' : 'No se pudo eliminar la cita'),
      );
    }
  };

  const slotStateLabel = (state) => {
    const map = {
      available: { en: 'Available', es: 'Libre' },
      past: { en: 'Past', es: 'Pasado' },
      closed: { en: 'Closed', es: 'Cerrado' },
      booked: { en: 'Scheduled', es: 'Programada' },
      completed: { en: 'Completed', es: 'Completado' },
      cancelled: { en: 'Cancelled', es: 'Cancelado' },
      no_show: { en: 'No show', es: 'No asistió' },
    };
    const x = map[state] || map.available;
    return lang === 'en' ? x.en : x.es;
  };

  return (
    <div className="appt-page">
      <div className="appt-header">
        <div>
          <h1>{lang === 'en' ? 'Appointments' : 'Citas'}</h1>
          <p>
            {lang === 'en'
              ? 'Manage your booking schedule, hours, and blocked dates'
              : 'Administra tu agenda, horarios y fechas bloqueadas'}
          </p>
        </div>
        {tab === 'today' && (
          <button type="button" className="appt-btn-primary" onClick={openModal}>
            + {lang === 'en' ? 'New Appointment' : 'Nueva Cita'}
          </button>
        )}
      </div>

      {banner?.type === 'error' && <div className="appt-alert appt-alert--error">{banner.msg}</div>}
      {banner?.type === 'ok' && <div className="appt-alert appt-alert--ok">{banner.msg}</div>}

      <div className="appt-tabs">
        <button
          type="button"
          className={`appt-tab ${tab === 'today' ? 'appt-tab--active' : ''}`}
          onClick={() => setTab('today')}
        >
          {lang === 'en' ? 'Today' : 'Hoy'}
        </button>
        <button
          type="button"
          className={`appt-tab ${tab === 'settings' ? 'appt-tab--active' : ''}`}
          onClick={() => setTab('settings')}
        >
          {lang === 'en' ? 'Settings' : 'Configuración'}
        </button>
        <button
          type="button"
          className={`appt-tab ${tab === 'schedule' ? 'appt-tab--active' : ''}`}
          onClick={() => setTab('schedule')}
        >
          {lang === 'en' ? 'Schedule' : 'Horarios'}
        </button>
        <button
          type="button"
          className={`appt-tab ${tab === 'blocked' ? 'appt-tab--active' : ''}`}
          onClick={() => setTab('blocked')}
        >
          {lang === 'en' ? 'Blocked Dates' : 'Fechas Bloqueadas'}
        </button>
      </div>

      {/* —— Tab: Today —— */}
      {tab === 'today' && (
        <div className="appt-panel">
          <div className="appt-toolbar">
            <label htmlFor="appt-date">
              {lang === 'en' ? 'Date' : 'Fecha'}
              <input
                id="appt-date"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
            </label>
          </div>

          {loadingDay ? (
            <div className="appt-loading">
              <div className="spinner" />
            </div>
          ) : dayViewError ? (
            <div className="appt-day-banner appt-day-banner--error">
              <p>{dayViewError}</p>
            </div>
          ) : dayView?.day_blocked ? (
            <div className="appt-day-banner appt-day-banner--blocked">
              <p>
                {lang === 'en'
                  ? 'This entire day is blocked. Remove it under Blocked Dates to allow appointments.'
                  : 'Este día está bloqueado por completo. Elimínalo en Fechas bloqueadas para permitir citas.'}
              </p>
            </div>
          ) : !dayView?.hours_configured ? (
            <div className="appt-day-banner appt-day-banner--muted">
              <p>
                {lang === 'en'
                  ? 'No appointment hours configured for this weekday. Add them in the Schedule tab.'
                  : 'No hay horario para este día de la semana. Configúralo en la pestaña Horarios.'}
              </p>
            </div>
          ) : (
            <>
              <p className="appt-day-meta">
                {lang === 'en' ? 'Availability grid:' : 'Disponibilidad (rejilla):'}{' '}
                <strong>{dayView.slot_duration} min</strong>
                {dayView.services_enabled ? (
                  <span>
                    {' '}
                    ({lang === 'en' ? 'grid interval from Settings' : 'intervalo de rejilla en Configuración'})
                  </span>
                ) : null}
                {dayView.window ? (
                  <>
                    {' · '}
                    {fmtTime12(dayView.window.start)} – {fmtTime12(dayView.window.end)}
                  </>
                ) : null}
              </p>
              <div className="appt-slot-grid">
                {(dayView.timeline || dayView.slots || [])
                  .map(normalizeDayItem)
                  .filter(Boolean)
                  .map((row, idx) => (
                    <div
                      key={`${row.kind}-${row.start_time}-${idx}`}
                      className={`appt-slot-card appt-slot-card--${row.state}`}
                    >
                      <div className="appt-slot-card__head">
                        <div className="appt-slot-card__time">
                          {fmtTime12(row.start_time)}
                          <span className="appt-slot-card__dash">–</span>
                          {fmtTime12(row.end_time)}
                        </div>
                        <span className="appt-slot-card__pill">{slotStateLabel(row.state)}</span>
                      </div>
                      {row.kind === 'appointment' && row.appointment?.service_name ? (
                        <div className="appt-slot-card__service">{row.appointment.service_name}</div>
                      ) : null}
                      {row.appointment?.client_name ? (
                        <div className="appt-slot-card__name">{row.appointment.client_name}</div>
                      ) : null}
                      <div className="appt-slot-card__actions">
                        {row.kind === 'free' && row.state === 'available' && (
                          <>
                            <button
                              type="button"
                              className="appt-btn-secondary appt-btn-secondary--sm"
                              onClick={() => openModalForSlot(row)}
                            >
                              {lang === 'en' ? 'Create' : 'Crear'}
                            </button>
                            <button
                              type="button"
                              className="appt-btn-secondary appt-btn-secondary--sm"
                              onClick={() => closeSlotAction(row)}
                            >
                              {lang === 'en' ? 'Close slot' : 'Cerrar'}
                            </button>
                          </>
                        )}
                        {row.kind === 'closed' && (
                          <button
                            type="button"
                            className="appt-btn-secondary appt-btn-secondary--sm"
                            onClick={() => reopenSlotAction(row)}
                          >
                            {lang === 'en' ? 'Reopen' : 'Reabrir'}
                          </button>
                        )}
                        {row.kind === 'appointment' &&
                          row.state === 'booked' &&
                          row.appointment?.id ? (
                          <>
                            <button
                              type="button"
                              className="appt-btn-ghost appt-btn-secondary--sm"
                              onClick={() => doComplete(row.appointment.id)}
                            >
                              {lang === 'en' ? 'Complete' : 'Completar'}
                            </button>
                            <button
                              type="button"
                              className="appt-btn-danger appt-btn-secondary--sm"
                              onClick={() => doCancel(row.appointment.id)}
                            >
                              {lang === 'en' ? 'Cancel' : 'Cancelar'}
                            </button>
                            <button
                              type="button"
                              className="appt-btn-reschedule"
                              onClick={() => openReschedule(row.appointment)}
                              title={lang === 'en' ? 'Reschedule' : 'Reprogramar'}
                            >
                              <span className="appt-btn-reschedule__icon" aria-hidden>
                                ↻
                              </span>
                              {lang === 'en' ? 'Reschedule' : 'Reprogramar'}
                            </button>
                          </>
                        ) : null}
                        {(row.state === 'cancelled' || row.state === 'no_show') && row.appointment?.id ? (
                          <button
                            type="button"
                            className="appt-btn-secondary appt-btn-secondary--sm"
                            onClick={() => releaseCancelledSlot(row.appointment.id)}
                          >
                            {lang === 'en' ? 'Make available' : 'Liberar horario'}
                          </button>
                        ) : null}
                      </div>
                    </div>
                  ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* —— Tab: Settings —— */}
      {tab === 'settings' && (
        <div className="appt-panel">
          {loadingSettings ? (
            <div className="appt-loading">
              <div className="spinner" />
            </div>
          ) : (
            <>
              <div className="appt-settings-grid">
                <div className="appt-settings-col">
                  <div className="appt-settings__toggle-wrap">
                    <div className="appt-settings__toggle-row">
                      <div className="appt-toggle-container">
                        <span className="appt-toggle-label">
                          {lang === 'en' ? 'Appointment system' : 'Sistema de citas'}
                        </span>
                        <button
                          type="button"
                          className={`appt-toggle-btn ${settings.enabled ? 'appt-toggle-btn--on' : 'appt-toggle-btn--off'}`}
                          onClick={() => setSettings((s) => ({ ...s, enabled: !s.enabled }))}
                        >
                          <span className="appt-toggle-btn__knob" />
                          <span className="appt-toggle-btn__text">{settings.enabled ? 'ON' : 'OFF'}</span>
                        </button>
                      </div>
                      <p className="appt-settings__hint">
                        {lang === 'en'
                          ? 'When enabled, your AI assistant will offer and book appointments with your customers.'
                          : 'Cuando está activo, tu asistente de IA ofrecerá y agendará citas con tus clientes.'}
                      </p>
                    </div>
                  </div>

                  <div className={`appt-field ${settings.services_enabled ? 'appt-services-disabled' : ''}`}>
                    <span className="appt-settings__hint appt-settings__hint--block">
                      {lang === 'en' ? 'Slot duration (minutes)' : 'Duración del turno (minutos)'}
                    </span>
                    <div className="appt-duration-row">
                      {SLOT_DURATIONS.map((m) => (
                        <button
                          key={m}
                          type="button"
                          className={`appt-pill ${settings.slot_duration === m ? 'appt-pill--on' : ''}`}
                          onClick={() => setSettings((s) => ({ ...s, slot_duration: m }))}
                          disabled={settings.services_enabled}
                        >
                          {m} min
                        </button>
                      ))}
                    </div>
                    {settings.services_enabled ? (
                      <p className="appt-settings__hint">
                        {lang === 'en'
                          ? 'Using individual service durations.'
                          : 'Usando duraciones de servicios individuales.'}
                      </p>
                    ) : null}
                  </div>

                  <div className="appt-field">
                    <label htmlFor="appt-advance">{lang === 'en' ? 'Advance booking (days)' : 'Anticipación (días)'}</label>
                    <input
                      id="appt-advance"
                      type="number"
                      min={1}
                      max={365}
                      value={settings.advance_days}
                      onChange={(e) =>
                        setSettings((s) => ({ ...s, advance_days: parseInt(e.target.value, 10) || 7 }))
                      }
                    />
                  </div>

                  <div className="appt-field">
                    <label htmlFor="appt-tz">{lang === 'en' ? 'Timezone' : 'Zona horaria'}</label>
                    <select
                      id="appt-tz"
                      value={settings.timezone}
                      onChange={(e) => setSettings((s) => ({ ...s, timezone: e.target.value }))}
                    >
                      {!TIMEZONES.includes(settings.timezone) && settings.timezone ? (
                        <option value={settings.timezone}>{settings.timezone.replace(/_/g, ' ')}</option>
                      ) : null}
                      {TIMEZONES.map((tz) => (
                        <option key={tz} value={tz}>
                          {tz.replace(/_/g, ' ')}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="appt-settings-col appt-settings-col--services">
                  <div className="appt-settings__toggle-row">
                    <div className="appt-toggle-container">
                      <span className="appt-toggle-label">
                        {lang === 'en' ? 'Custom services' : 'Servicios personalizados'}
                      </span>
                      <button
                        type="button"
                        className={`appt-toggle-btn ${settings.services_enabled ? 'appt-toggle-btn--on' : 'appt-toggle-btn--off'}`}
                        onClick={() => setSettings((s) => ({ ...s, services_enabled: !s.services_enabled }))}
                      >
                        <span className="appt-toggle-btn__knob" />
                        <span className="appt-toggle-btn__text">{settings.services_enabled ? 'ON' : 'OFF'}</span>
                      </button>
                    </div>
                    <p className="appt-settings__hint">
                      {lang === 'en'
                        ? 'When enabled, you can define individual services with different durations. Your AI will ask customers which service they need.'
                        : 'Cuando está activo, puedes definir servicios individuales con diferentes duraciones. Tu IA le preguntará al cliente qué servicio necesita.'}
                    </p>
                  </div>

                  {settings.services_enabled ? (
                    <>
                      <div ref={serviceFormRef} className="appt-service-form">
                        <strong>
                          {editingServiceId
                            ? lang === 'en'
                              ? 'Edit service'
                              : 'Editar servicio'
                            : lang === 'en'
                              ? 'Add service'
                              : 'Agregar servicio'}
                        </strong>
                        <div className="appt-field" style={{ marginBottom: 0 }}>
                          <label>{lang === 'en' ? 'Name' : 'Nombre'}</label>
                          <input
                            value={serviceForm.name}
                            onChange={(e) => setServiceForm((f) => ({ ...f, name: e.target.value }))}
                          />
                        </div>
                        <div className="appt-field" style={{ marginBottom: 0 }}>
                          <label>{lang === 'en' ? 'Duration (minutes)' : 'Duración (minutos)'}</label>
                          <input
                            type="number"
                            min={1}
                            value={serviceForm.duration}
                            onChange={(e) =>
                              setServiceForm((f) => ({ ...f, duration: parseInt(e.target.value, 10) || 0 }))
                            }
                          />
                        </div>
                        <div className="appt-field" style={{ marginBottom: 0 }}>
                          <label>{lang === 'en' ? 'Price (optional)' : 'Precio (opcional)'}</label>
                          <input
                            value={serviceForm.price}
                            onChange={(e) => setServiceForm((f) => ({ ...f, price: e.target.value }))}
                            placeholder={lang === 'en' ? 'e.g. 25' : 'ej. 25'}
                          />
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            className="appt-btn-primary"
                            onClick={saveService}
                            disabled={savingService}
                          >
                            {savingService
                              ? '...'
                              : editingServiceId
                                ? lang === 'en'
                                  ? 'Update'
                                  : 'Actualizar'
                                : lang === 'en'
                                  ? 'Add'
                                  : 'Agregar'}
                          </button>
                          {editingServiceId ? (
                            <button
                              type="button"
                              className="appt-btn-secondary"
                              onClick={() => {
                                setEditingServiceId(null);
                                setServiceForm({ name: '', duration: 30, price: '' });
                              }}
                            >
                              {lang === 'en' ? 'Cancel' : 'Cancelar'}
                            </button>
                          ) : null}
                        </div>
                      </div>

                      {loadingServices ? (
                        <p className="appt-settings__hint">{lang === 'en' ? 'Loading…' : 'Cargando…'}</p>
                      ) : (
                        <div className="appt-services-list">
                          {servicesList.filter((x) => Number(x.is_active) !== 0).length === 0 ? (
                            <p className="appt-settings__hint">
                              {lang === 'en' ? 'No services yet.' : 'Aún no hay servicios.'}
                            </p>
                          ) : (
                            servicesList
                              .filter((x) => Number(x.is_active) !== 0)
                              .map((s) => (
                                <div key={s.id} className="appt-service-row">
                                  <div>
                                    <strong>{s.name}</strong>
                                    <div className="appt-service-row__meta">
                                      {s.duration} min
                                      {s.price != null && s.price !== ''
                                        ? ` · $${Number(s.price).toFixed(2)}`
                                        : ''}
                                    </div>
                                  </div>
                                  <div className="appt-service-row__actions">
                                    <button
                                      type="button"
                                      className="appt-btn-secondary appt-btn-secondary--sm"
                                      onClick={() => startEditService(s)}
                                    >
                                      {lang === 'en' ? 'Edit' : 'Editar'}
                                    </button>
                                    <button
                                      type="button"
                                      className="appt-btn-danger appt-btn-secondary--sm"
                                      onClick={() => deleteService(s.id)}
                                    >
                                      {lang === 'en' ? 'Remove' : 'Quitar'}
                                    </button>
                                  </div>
                                </div>
                              ))
                          )}
                        </div>
                      )}

                      <div className="appt-field" style={{ marginTop: '1rem' }}>
                        <span className="appt-settings__hint appt-settings__hint--block">
                          {lang === 'en' ? 'Grid display interval' : 'Intervalo de visualización'}
                        </span>
                        <p className="appt-settings__hint" style={{ marginTop: 0, marginBottom: '0.65rem' }}>
                          {lang === 'en'
                            ? 'How to divide available time slots in the calendar view. Each appointment still uses the selected service duration.'
                            : 'Cómo dividir los espacios disponibles en la vista de calendario. Cada cita sigue usando la duración del servicio elegido.'}
                        </p>
                        <div className="appt-duration-row">
                          {GRID_DURATIONS.map((m) => (
                            <button
                              key={m}
                              type="button"
                              className={`appt-pill ${settings.grid_duration === m ? 'appt-pill--on' : ''}`}
                              onClick={() => setSettings((s) => ({ ...s, grid_duration: m }))}
                            >
                              {m} min
                            </button>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : null}
                </div>
              </div>

              <button type="button" className="appt-btn-primary" onClick={saveSettings} disabled={savingSettings}>
                {savingSettings ? '...' : lang === 'en' ? 'Save' : 'Guardar'}
              </button>
            </>
          )}
        </div>
      )}

      {/* —— Tab: Schedule —— */}
      {tab === 'schedule' && (
        <div className="appt-panel">
          <p className="appt-sched-hint">
            {lang === 'en'
              ? 'Tip: Monday–Friday 9:00–17:00 is a common setup. Toggle days off to exclude weekends.'
              : 'Sugerencia: Lunes a viernes 9:00–17:00 es una configuración habitual. Desactiva días para excluir fines de semana.'}
          </p>
          {loadingHours ? (
            <div className="appt-loading">
              <div className="spinner" />
            </div>
          ) : (
            <>
              <div className="appt-schedule-desktop">
                <table className="appt-schedule-table">
                  <thead>
                    <tr>
                      <th>{lang === 'en' ? 'Day' : 'Día'}</th>
                      <th>{lang === 'en' ? 'Active' : 'Activo'}</th>
                      <th>{lang === 'en' ? 'Start' : 'Inicio'}</th>
                      <th>{lang === 'en' ? 'End' : 'Fin'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hours.map((row) => (
                      <tr
                        key={row.day_of_week}
                        className={!row.is_active ? 'appt-row--disabled' : ''}
                      >
                        <td>{labels[row.day_of_week]}</td>
                        <td>
                          <input
                            type="checkbox"
                            checked={!!row.is_active}
                            onChange={(e) =>
                              updateHour(row.day_of_week, 'is_active', e.target.checked ? 1 : 0)
                            }
                            style={{ accentColor: 'var(--accent-green)', cursor: 'pointer' }}
                          />
                        </td>
                        <td>
                          <input
                            type="time"
                            value={row.start_time}
                            disabled={!row.is_active}
                            onChange={(e) => updateHour(row.day_of_week, 'start_time', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="time"
                            value={row.end_time}
                            disabled={!row.is_active}
                            onChange={(e) => updateHour(row.day_of_week, 'end_time', e.target.value)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="appt-schedule-cards">
                {hours.map((row) => (
                  <div
                    key={`card-${row.day_of_week}`}
                    className={`appt-schedule-card ${!row.is_active ? 'appt-schedule-card--inactive' : ''}`}
                  >
                    <div className="appt-schedule-card__day">{labels[row.day_of_week]}</div>
                    <label className="appt-schedule-card__field">
                      <span className="appt-schedule-card__label">{lang === 'en' ? 'Active' : 'Activo'}</span>
                      <input
                        type="checkbox"
                        checked={!!row.is_active}
                        onChange={(e) =>
                          updateHour(row.day_of_week, 'is_active', e.target.checked ? 1 : 0)
                        }
                        style={{ accentColor: 'var(--accent-green)', cursor: 'pointer', width: 'auto' }}
                      />
                    </label>
                    <label className="appt-schedule-card__field">
                      <span className="appt-schedule-card__label">{lang === 'en' ? 'Start' : 'Inicio'}</span>
                      <input
                        className="appt-schedule-card__time"
                        type="time"
                        value={row.start_time}
                        disabled={!row.is_active}
                        onChange={(e) => updateHour(row.day_of_week, 'start_time', e.target.value)}
                      />
                    </label>
                    <label className="appt-schedule-card__field">
                      <span className="appt-schedule-card__label">{lang === 'en' ? 'End' : 'Fin'}</span>
                      <input
                        className="appt-schedule-card__time"
                        type="time"
                        value={row.end_time}
                        disabled={!row.is_active}
                        onChange={(e) => updateHour(row.day_of_week, 'end_time', e.target.value)}
                      />
                    </label>
                  </div>
                ))}
              </div>

              <button
                type="button"
                className="appt-btn-primary appt-schedule-save"
                onClick={saveHours}
                disabled={savingHours}
              >
                {savingHours ? '...' : lang === 'en' ? 'Save Schedule' : 'Guardar Horarios'}
              </button>
            </>
          )}
        </div>
      )}

      {/* —— Tab: Blocked —— */}
      {tab === 'blocked' && (
        <div className="appt-panel">
          {loadingBlocked ? (
            <div className="appt-loading">
              <div className="spinner" />
            </div>
          ) : (
            <>
              {blocked.length === 0 ? (
                <p style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>
                  {lang === 'en' ? 'No blocked dates' : 'No hay fechas bloqueadas'}
                </p>
              ) : (
                <div className="appt-blocked-list">
                  {blocked.map((b) => (
                    <div className="appt-blocked-item" key={b.id}>
                      <span className="appt-blocked-item__date">{b.blocked_date}</span>
                      <span className="appt-blocked-item__reason">{b.reason || '—'}</span>
                      <button type="button" className="appt-btn-danger" onClick={() => removeBlocked(b.id)}>
                        {lang === 'en' ? 'Remove' : 'Quitar'}
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="appt-inline-form">
                <div className="appt-field" style={{ marginBottom: 0, flex: '0 0 auto' }}>
                  <label htmlFor="blk-date">{lang === 'en' ? 'Date' : 'Fecha'}</label>
                  <input
                    id="blk-date"
                    type="date"
                    value={blockedDate}
                    onChange={(e) => setBlockedDate(e.target.value)}
                  />
                </div>
                <div className="appt-field" style={{ marginBottom: 0, flex: '1 1 200px' }}>
                  <label htmlFor="blk-reason">{lang === 'en' ? 'Reason (optional)' : 'Motivo (opcional)'}</label>
                  <input
                    id="blk-reason"
                    type="text"
                    value={blockedReason}
                    onChange={(e) => setBlockedReason(e.target.value)}
                    placeholder={lang === 'en' ? 'Holiday, closed, etc.' : 'Festivo, cierre, etc.'}
                  />
                </div>
                <button
                  type="button"
                  className="appt-btn-primary"
                  onClick={addBlocked}
                  disabled={addingBlocked || !blockedDate}
                >
                  {addingBlocked ? '...' : lang === 'en' ? 'Block Date' : 'Bloquear Fecha'}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {modalOpen && (
        <div
          className="appt-modal-overlay"
          role="presentation"
          onClick={(e) => e.target === e.currentTarget && setModalOpen(false)}
        >
          <div className="appt-modal" role="dialog" aria-modal="true">
            <h2>{lang === 'en' ? 'New appointment' : 'Nueva cita'}</h2>
            <div className="appt-field">
              <label>{lang === 'en' ? 'Customer name' : 'Nombre del cliente'}</label>
              <input
                value={modalForm.client_name}
                onChange={(e) => setModalForm((f) => ({ ...f, client_name: e.target.value }))}
              />
            </div>
            <div className="appt-field">
              <label>{lang === 'en' ? 'Phone (optional)' : 'Teléfono (opcional)'}</label>
              <input
                value={modalForm.client_phone}
                onChange={(e) => setModalForm((f) => ({ ...f, client_phone: e.target.value }))}
              />
            </div>
            <div className="appt-field">
              <label>{lang === 'en' ? 'Date' : 'Fecha'}</label>
              <input
                type="date"
                value={modalForm.appointment_date}
                onChange={(e) => setModalForm((f) => ({ ...f, appointment_date: e.target.value }))}
              />
            </div>
            {settings.services_enabled && servicesList.filter((s) => Number(s.is_active) !== 0).length > 0 ? (
              <div className="appt-field">
                <label>{lang === 'en' ? 'Service' : 'Servicio'}</label>
                <select
                  value={modalForm.service_id}
                  onChange={(e) => setModalForm((f) => ({ ...f, service_id: e.target.value }))}
                >
                  <option value="">{lang === 'en' ? 'Select service' : 'Seleccionar servicio'}</option>
                  {servicesList
                    .filter((s) => Number(s.is_active) !== 0)
                    .map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name} ({s.duration} min)
                      </option>
                    ))}
                </select>
              </div>
            ) : null}
            <div className="appt-field">
              <label>{lang === 'en' ? 'Time slot' : 'Horario'}</label>
              {slotsLoading ? (
                <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem' }}>{lang === 'en' ? 'Loading slots…' : 'Cargando horarios…'}</p>
              ) : slotOptions.length === 0 ? (
                <p style={{ color: 'var(--accent-yellow)', fontSize: '0.88rem' }}>
                  {lang === 'en'
                    ? 'No slots available for this date.'
                    : 'No hay horarios para esta fecha.'}
                </p>
              ) : (
                <select
                  value={modalForm.appointment_time}
                  onChange={(e) => setModalForm((f) => ({ ...f, appointment_time: e.target.value }))}
                >
                  <option value="">{lang === 'en' ? 'Select time' : 'Seleccionar hora'}</option>
                  {slotOptions.map((s) => (
                    <option key={s} value={s}>
                      {fmtTime12(s)}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="appt-field">
              <label>{lang === 'en' ? 'Notes (optional)' : 'Notas (opcional)'}</label>
              <textarea
                value={modalForm.notes}
                onChange={(e) => setModalForm((f) => ({ ...f, notes: e.target.value }))}
              />
            </div>
            <div className="appt-modal__actions">
              <button type="button" className="appt-btn-secondary" onClick={() => setModalOpen(false)}>
                {lang === 'en' ? 'Cancel' : 'Cancelar'}
              </button>
              <button type="button" className="appt-btn-primary" onClick={saveNewAppointment} disabled={savingAppt}>
                {savingAppt ? '...' : lang === 'en' ? 'Save' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {rescheduleOpen && rescheduleAppt && (
        <div
          className="appt-modal-overlay"
          role="presentation"
          onClick={(e) => e.target === e.currentTarget && closeReschedule()}
        >
          <div className="appt-modal appt-modal--reschedule" role="dialog" aria-modal="true">
            <h2>
              {lang === 'en' ? 'Reschedule appointment' : 'Reprogramar cita'}{' '}
              <span className="appt-modal__subtitle">
                ({rescheduleAppt.client_name || (lang === 'en' ? 'Customer' : 'Cliente')})
              </span>
            </h2>
            <p className="appt-reschedule-current">
              {lang === 'en' ? 'Current' : 'Actual'}:{' '}
              <strong>
                {formatApptDateIso(rescheduleAppt)} · {fmtTime12(rescheduleAppt.appointment_time)}
              </strong>
              {rescheduleAppt.service_name ? ` · ${rescheduleAppt.service_name}` : ''}
            </p>
            <div className="appt-field">
              <label>{lang === 'en' ? 'New date' : 'Nueva fecha'}</label>
              <input
                type="date"
                value={rescheduleForm.new_date}
                onChange={(e) => setRescheduleForm((f) => ({ ...f, new_date: e.target.value }))}
              />
            </div>
            {settings.services_enabled && servicesList.filter((s) => Number(s.is_active) !== 0).length > 0 ? (
              <div className="appt-field">
                <label>{lang === 'en' ? 'Service (optional)' : 'Servicio (opcional)'}</label>
                <select
                  value={rescheduleForm.new_service_id}
                  onChange={(e) => setRescheduleForm((f) => ({ ...f, new_service_id: e.target.value }))}
                >
                  <option value="">{lang === 'en' ? 'Keep current' : 'Mantener actual'}</option>
                  {servicesList
                    .filter((s) => Number(s.is_active) !== 0)
                    .map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name} ({s.duration} min)
                      </option>
                    ))}
                </select>
              </div>
            ) : null}
            <div className="appt-field">
              <label>{lang === 'en' ? 'New time' : 'Nueva hora'}</label>
              {rescheduleSlotsLoading ? (
                <p className="appt-reschedule-hint">{lang === 'en' ? 'Loading slots…' : 'Cargando horarios…'}</p>
              ) : rescheduleSlots.length === 0 ? (
                <p className="appt-reschedule-hint appt-reschedule-hint--warn">
                  {lang === 'en'
                    ? 'No slots available for this date.'
                    : 'No hay horarios para esta fecha.'}
                </p>
              ) : (
                <select
                  value={rescheduleForm.new_time}
                  onChange={(e) => setRescheduleForm((f) => ({ ...f, new_time: e.target.value }))}
                >
                  <option value="">{lang === 'en' ? 'Select time' : 'Seleccionar hora'}</option>
                  {rescheduleSlots.map((s) => (
                    <option key={s} value={String(s).slice(0, 5)}>
                      {fmtTime12(s)}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="appt-modal__actions">
              <button type="button" className="appt-btn-secondary" onClick={closeReschedule}>
                {lang === 'en' ? 'Cancel' : 'Cancelar'}
              </button>
              <button
                type="button"
                className="appt-btn-primary"
                onClick={submitReschedule}
                disabled={savingReschedule || !rescheduleForm.new_time}
              >
                {savingReschedule ? '...' : lang === 'en' ? 'Reschedule' : 'Reprogramar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
